// Use apollo vue useQuery and make it blocking

import type { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { useQuery } from '@vue/apollo-composable';
import type {
  DocumentParameter,
  OnResultContext,
  VariablesParameter,
} from '@vue/apollo-composable/dist/useQuery.js';

// Adds messages only in a dev environment, see https://www.apollographql.com/docs/react/errors/#what-is-this-page
if (process.env.NODE_ENV !== 'production') {
  loadDevMessages();
  loadErrorMessages();
}

export default <
  TResult = never,
  TVariables extends OperationVariables = OperationVariables,
>(
  document: DocumentParameter<TResult, TVariables>,
  variables?: VariablesParameter<TVariables>,
): Promise<ApolloQueryResult<TResult>> => {
  return new Promise((resolve, reject) => {
    // Almost every caller invokes this from inside a `useAsyncData` handler,
    // which is async and therefore outside any active effect scope. In that case
    // `useQuery` warns that "the query will not be automatically stopped" and
    // never tears the watchQuery down — while the server ApolloClient is a
    // module-level singleton shared by every SSR request (see plugins/apollo.ts).
    // Each request was leaking live queries into a process-lifetime client
    // (TV-1845). Stopping explicitly is safe: the promise settles on the first
    // emission, so no caller ever observed a later one.
    const { onResult, onError, stop } = useQuery<TResult, TVariables>(
      document,
      variables,
      {
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      },
    );

    onResult((result: ApolloQueryResult<TResult>, context: OnResultContext) => {
      stop();
      resolve(result);
    });

    onError((error) => {
      stop();
      reject(error);
    });
  });
};
